/**
 * Хранилище токенов сессии.
 *
 * Access-токен живёт **только в памяти процесса** — не уходит в localStorage,
 * не пишется в логи. Refresh-токен и device-id — в localStorage (на ранней
 * стадии). При переходе на httpOnly-cookie для refresh поле `refreshToken`
 * перестанет использоваться, методы останутся для API-совместимости.
 *
 * Различаем два device-id:
 * - **client device-id** (`getClientDeviceId`) — UUID, сгенерированный браузером,
 *   живёт постоянно. Передаём в `/verify` как стабильный идентификатор устройства
 *   для дедупликации регистраций на бэке.
 * - **backend device-id** (`getBackendDeviceId`) — внутренний UUID `Device.id`
 *   с бэкенда, возвращается в `TokenResponse.deviceId`. К нему привязан refresh,
 *   его проверяет `JwtAuthFilter`. Передаём в `/refresh` и `/logout`.
 */

const REFRESH_KEY = 'p2pa.refresh';
const CLIENT_DEVICE_ID_KEY = 'p2pa.client-device-id';
const BACKEND_DEVICE_ID_KEY = 'p2pa.backend-device-id';

let accessToken: string | null = null;
let accessExpiresAt: number | null = null;

/**
 * Кладёт access-токен в память и считает срок действия.
 *
 * @param token строка JWT
 * @param expiresInSeconds сколько секунд жив (из `TokenResponse`)
 */
export function setAccess(token: string, expiresInSeconds: number): void {
  accessToken = token;
  accessExpiresAt = Date.now() + expiresInSeconds * 1000;
}

/**
 * Возвращает текущий access-токен или `null`, если его нет.
 *
 * Не проверяет срок — для этого есть `isAccessExpired`. Это разделение
 * нужно, чтобы интерсептор мог отдельно решить: вызвать /refresh или
 * сразу подставить заголовок.
 */
export function getAccess(): string | null {
  return accessToken;
}

/**
 * Истёк ли access-токен (с запасом в 5 секунд от часов клиента).
 */
export function isAccessExpired(): boolean {
  if (accessExpiresAt === null) return true;
  return Date.now() >= accessExpiresAt - 5000;
}

/**
 * Сохраняет refresh-токен в localStorage.
 */
export function setRefresh(token: string): void {
  localStorage.setItem(REFRESH_KEY, token);
}

/**
 * Возвращает сохранённый refresh-токен или `null`.
 */
export function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

/**
 * Очищает все токены и backend device-id (logout / fatal auth-ошибка).
 *
 * Клиентский device-id оставляем — он стабильный идентификатор браузера и должен
 * пережить logout, чтобы бэк при следующем входе нашёл то же устройство.
 */
export function clearTokens(): void {
  accessToken = null;
  accessExpiresAt = null;
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(BACKEND_DEVICE_ID_KEY);
}

/**
 * Возвращает (или создаёт при первом вызове) клиентский device-id браузера.
 *
 * Используется только в `/api/auth/otp/verify` как стабильный идентификатор
 * устройства (бэк по нему дедуплицирует регистрации в таблице `devices`).
 */
export function getClientDeviceId(): string {
  let id = localStorage.getItem(CLIENT_DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CLIENT_DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Сохраняет бэковый `Device.id` из `TokenResponse.deviceId`.
 *
 * Используется в `/api/auth/refresh` и `/api/auth/logout` — без него
 * бэк отвечает 401 `INVALID_REFRESH` (refresh привязан именно к этому id).
 */
export function setBackendDeviceId(id: string): void {
  localStorage.setItem(BACKEND_DEVICE_ID_KEY, id);
}

/**
 * Возвращает сохранённый бэковый device-id или `null`, если его ещё нет
 * (до первого успешного verify).
 */
export function getBackendDeviceId(): string | null {
  return localStorage.getItem(BACKEND_DEVICE_ID_KEY);
}
